/* eslint-disable react/prop-types */
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest, initializeSession, setCsrfToken } from '../Services/api';

export const AdminContext = createContext(null);

export default function AdminContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [cartBusy, setCartBusy] = useState(false);

  const refreshCart = useCallback(async () => {
    const data = await apiRequest('/cart/index.php');
    setItems(data.items);
    return data.items;
  }, []);

  useEffect(() => {
    let active = true;
    const loadSession = async () => {
      try {
        const sessionUser = await initializeSession();
        if (!active) return;
        setUser(sessionUser);
        if (sessionUser) {
          const data = await apiRequest('/cart/index.php');
          if (active) setItems(data.items);
        }
      } catch (error) {
        console.error('Unable to initialize session:', error);
      } finally {
        if (active) setAuthLoading(false);
      }
    };
    loadSession();
    return () => { active = false; };
  }, []);

  const login = useCallback(async (credentials) => {
    const data = await apiRequest('/auth/login.php', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    setUser(data.user);
    const cart = await apiRequest('/cart/index.php');
    setItems(cart.items);
    return data.user;
  }, []);

  const signup = useCallback(async (details) => {
    const data = await apiRequest('/auth/signup.php', {
      method: 'POST',
      body: JSON.stringify(details),
    });
    setUser(data.user);
    setItems([]);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    const data = await apiRequest('/auth/logout.php', { method: 'POST' });
    setCsrfToken(data.csrfToken);
    setUser(null);
    setItems([]);
  }, []);

  const syncUser = useCallback((nextUser) => {
    setUser(nextUser);
  }, []);

  const addItems = useCallback(async (product) => {
    setCartBusy(true);
    try {
      const currentCart = await apiRequest('/cart/index.php');
      const selectedSizeId = Number(product.selectedSizeId);
      const existingItem = currentCart.items.find((item) => Number(item.productSizeId) === selectedSizeId);
      const requestedQuantity = Number(product.selectedQuantity);
      const nextQuantity = existingItem ? Number(existingItem.selectedQuantity) + requestedQuantity : requestedQuantity;
      if (nextQuantity > 100) throw new Error('A cart line cannot exceed 100 items.');
      const data = existingItem
        ? await apiRequest('/cart/index.php', { method: 'PATCH', body: JSON.stringify({ id: existingItem.id, quantity: nextQuantity }) })
        : await apiRequest('/cart/index.php', {
          method: 'POST',
          body: JSON.stringify({ productId: Number(product.id), productSizeId: selectedSizeId, quantity: requestedQuantity }),
        });
      setItems(data.items);
      return data.items;
    } finally {
      setCartBusy(false);
    }
  }, []);

  const updateItem = useCallback(async (id, quantity) => {
    setCartBusy(true);
    try {
      const data = await apiRequest('/cart/index.php', {
        method: 'PATCH',
        body: JSON.stringify({ id: Number(id), quantity: Number(quantity) }),
      });
      setItems(data.items);
      return data.items;
    } finally {
      setCartBusy(false);
    }
  }, []);

  const removeItems = useCallback(async (itemId) => {
    setCartBusy(true);
    try {
      const data = await apiRequest(`/cart/index.php?id=${encodeURIComponent(itemId)}`, { method: 'DELETE' });
      setItems(data.items);
      return data.items;
    } finally {
      setCartBusy(false);
    }
  }, []);

  const canAdmin = user?.role === 'admin';
  const value = useMemo(() => ({
    user,
    authLoading,
    login,
    signup,
    logout,
    items,
    setItems,
    addItems,
    updateItem,
    removeItems,
    cartBusy,
    refreshCart,
    syncUser,
    canAdmin,
  }), [user, authLoading, login, signup, logout, items, addItems, updateItem, removeItems, cartBusy, refreshCart, syncUser, canAdmin]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
